import type { WaypointInput } from '../types/jobs';
import backgroundLocation, { DEFAULT_FIX_INTERVAL_MS, SPRAY_FIX_INTERVAL_MS } from './backgroundLocation';
import waypointQueue, { TERMINAL_UPLOAD_CODES } from './waypointQueue';

export type TrackingEntity = { type: 'job' | 'spray'; id: number };

export interface TrackingState {
  entity: TrackingEntity | null;
  tracking: boolean;
  waypointCount: number;
  pendingCount: number;
  lastLocation: { lat: number; lng: number } | null;
  gpsStatus: 'good' | 'poor' | 'unavailable';
  currentSpeedKmh: number | null;
  recentSpeeds: (number | null)[];
  error: string | null;
}

type Listener = (s: TrackingState) => void;

const STORAGE_KEY = 'factree_active_tracking';
const SPEED_BUFFER_SIZE = 20;
const PENDING_POLL_MS = 5_000;
// Watchdog: if tracking is supposedly active but no waypoint has arrived in
// this many ms, try to restart the platform GPS. The underlying cause is
// usually a stale Capgo callback after a JS context reload that we somehow
// didn't cover, or a silent native-side failure.
const WATCHDOG_INTERVAL_MS = 30_000;
const WATCHDOG_STALE_MS = 90_000;
const WATCHDOG_MAX_RESTARTS = 3;

/**
 * A spray's per-tank area and pass width are measured off its fixes, so it
 * records three times as often as a job. See SPRAY_FIX_INTERVAL_MS.
 */
function fixIntervalFor(entity: TrackingEntity): number {
  return entity.type === 'spray' ? SPRAY_FIX_INTERVAL_MS : DEFAULT_FIX_INTERVAL_MS;
}

/**
 * App-level GPS tracking controller. Lives outside React so navigation
 * between pages does not tear down the foreground service or drop points.
 *
 * Lifecycle:
 *   start(entity) → subscribe backgroundLocation, start queue auto-sync
 *   ... points flow through while pages mount/unmount freely ...
 *   stop()       → drain queue (up to 10 s), stop bg location, stop auto-sync
 *
 * Rehydration: persists `{entity}` to localStorage on start. `TrackingWatcher`
 * reads this on app mount and re-subscribes callbacks so tracking continues
 * after a reload (Capacitor foreground service keeps GPS running).
 */
class TrackingController {
  private state: TrackingState = {
    entity: null,
    tracking: false,
    waypointCount: 0,
    pendingCount: 0,
    lastLocation: null,
    gpsStatus: 'unavailable',
    currentSpeedKmh: null,
    recentSpeeds: [],
    error: null,
  };

  private listeners = new Set<Listener>();
  private speedBuffer: (number | null)[] = [];
  private pendingTimer: ReturnType<typeof setInterval> | null = null;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  private lastWaypointAt: number = 0;
  private watchdogRestartAttempts: number = 0;
  private locationSubscribed = false;
  private queueStatusUnsub: (() => void) | null = null;
  private lastReactedQueueErrorAt: number = 0;

  getState(): TrackingState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  /**
   * Begin tracking for a given entity. Safe to call when already tracking —
   * if the same entity is active, this is a no-op. If a different entity is
   * active, the previous one is stopped first (drain).
   */
  async start(entity: TrackingEntity): Promise<void> {
    if (this.state.entity && (this.state.entity.type !== entity.type || this.state.entity.id !== entity.id)) {
      await this.stop();
    }
    if (this.state.tracking && this.state.entity?.type === entity.type && this.state.entity.id === entity.id) {
      return;
    }

    this.setState({
      entity,
      tracking: true,
      waypointCount: 0,
      pendingCount: 0,
      lastLocation: null,
      gpsStatus: 'unavailable',
      currentSpeedKmh: null,
      recentSpeeds: [],
      error: null,
    });
    this.speedBuffer = [];

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entity));
    } catch {
      /* storage full / disabled — ignore */
    }

    this.attachLocationListener();
    this.attachQueueStatusListener();
    waypointQueue.startAutoSync();

    try {
      await backgroundLocation.start(fixIntervalFor(entity));
    } catch (e) {
      this.setState({ tracking: false, error: `GPS start failed: ${e instanceof Error ? e.message : String(e)}` });
      this.detachLocationListener();
      waypointQueue.stopAutoSync();
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      return;
    }

    if (this.pendingTimer == null) {
      this.pendingTimer = setInterval(() => this.refreshPendingCount(), PENDING_POLL_MS);
    }
    this.startWatchdog();
  }

  /**
   * Subscribe to the upload queue's status. When the server returns 409
   * (assignment paused / spray not in progress) for the entity we are
   * currently capturing, stop GPS capture but keep the queue alive — so the
   * pending points sit ready to upload the moment the operator resumes.
   *
   * 409s for OTHER entities (e.g. an orphan job:23 still queued while we're
   * tracking job:24) are intentionally ignored here: the records keep
   * waiting and only that entity's tracking would self-heal.
   */
  private attachQueueStatusListener(): void {
    if (this.queueStatusUnsub != null) return;
    this.queueStatusUnsub = waypointQueue.subscribeStatus((s) => {
      const err = s.lastError;
      if (!err) return;

      // Auth failure — the session is dead. Halt the tracker and drop the
      // entity from storage; queued points stay for after sign-in. Without
      // this, a single expired token snowballs into a tight 401 loop because
      // the queue keeps retrying and the API interceptor keeps redirecting.
      if (err.status === 401) {
        if (err.at <= this.lastReactedQueueErrorAt) return;
        this.lastReactedQueueErrorAt = err.at;
        console.warn('[trackingController] upload returned 401 — halting tracker; queued points kept for after sign-in');
        void this.halt();
        return;
      }

      if (err.status !== 409 && !TERMINAL_UPLOAD_CODES.has(err.code ?? '')) return;
      // De-dupe: subscribeStatus fires on every setStatus call (including
      // unrelated ones), so only react to a fresh 409 timestamp.
      if (err.at <= this.lastReactedQueueErrorAt) return;
      const cur = this.state.entity;
      if (!cur || err.entityType !== cur.type || err.entityId !== cur.id) return;
      this.lastReactedQueueErrorAt = err.at;

      // Closed, not paused. Telling the operator to resume a job that is
      // finished invites them to try a recovery that cannot work, and pausing
      // leaves the tracker half-armed on an entity that no longer exists.
      // The queue has already discarded the points, so there is nothing left
      // to drain either — stop outright.
      if (TERMINAL_UPLOAD_CODES.has(err.code ?? '')) {
        console.warn(
          `[trackingController] ${err.entityType}:${err.entityId} is closed ` +
          `(${err.code}) — stopping tracking`,
        );
        this.setState({
          error: 'This job is finished on the server. GPS tracking has stopped.',
        });
        void this.stop();
        return;
      }

      console.warn(
        `[trackingController] server reported ${err.entityType}:${err.entityId} ` +
        `is no longer active (409) — pausing capture. Queued points will ` +
        `upload when the operator resumes.`,
      );
      this.setState({
        error: 'Job paused on the server. GPS stopped — resume the job to continue tracking.',
      });
      void this.pauseCapture();
    });
  }

  private detachQueueStatusListener(): void {
    if (this.queueStatusUnsub != null) {
      this.queueStatusUnsub();
      this.queueStatusUnsub = null;
    }
  }

  /**
   * Pause GPS capture but keep the queue alive so pending points continue
   * uploading in the background. The entity is retained so `resume` /
   * subsequent `start()` for the same entity can pick up where we left off,
   * and the operator keeps seeing `pendingCount`.
   *
   * Use this when the operator taps Pause on the job; use `stop()` only for
   * complete / cancel / switching entities.
   */
  async pauseCapture(): Promise<void> {
    if (!this.state.tracking) return;

    this.detachLocationListener();
    try {
      await backgroundLocation.stop();
    } catch (e) {
      console.warn('[trackingController] pauseCapture: backgroundLocation.stop failed', e);
    }
    this.stopWatchdog();

    // Clear the persisted entity so a kill-while-paused doesn't auto-resume
    // tracking on next app launch (which would re-show the foreground-service
    // notification on Dashboard with no active job/spray). Resuming the job
    // calls start() again, which re-writes the key.
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }

    // Intentionally leave waypointQueue.startAutoSync running and the entity
    // set in memory — so points still in IndexedDB keep uploading and the UI
    // continues to reflect their progress for the rest of this session.
    this.setState({
      tracking: false,
      gpsStatus: 'unavailable',
      currentSpeedKmh: null,
    });
  }

  /**
   * Stop tracking. Drains the queue (up to 10 s) before shutting GPS down.
   */
  async stop(): Promise<void> {
    if (!this.state.tracking && this.state.entity == null) return;

    // Drain queued waypoints before tearing anything down.
    try {
      await waypointQueue.flush({ drain: true });
    } catch (e) {
      console.warn('[trackingController] drain failed', e);
    }

    this.detachLocationListener();
    this.detachQueueStatusListener();
    try {
      await backgroundLocation.stop();
    } catch (e) {
      console.warn('[trackingController] backgroundLocation.stop failed', e);
    }
    waypointQueue.stopAutoSync();

    if (this.pendingTimer != null) {
      clearInterval(this.pendingTimer);
      this.pendingTimer = null;
    }
    this.stopWatchdog();

    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }

    // Final pending count (should normally be 0 after drain).
    let finalPending = 0;
    const entity = this.state.entity;
    if (entity != null) {
      try {
        finalPending = await waypointQueue.pendingCount(entity.type, entity.id);
      } catch { /* ignore */ }
    }

    this.setState({
      entity: null,
      tracking: false,
      pendingCount: finalPending,
      gpsStatus: 'unavailable',
      currentSpeedKmh: null,
    });
    this.speedBuffer = [];
  }

  /**
   * Hard halt — used when we know the session is dead (401) or when
   * `TrackingWatcher` observes an unauthenticated auth context.
   * Stops GPS, stops the upload queue and drops the persisted entity.
   * Does NOT redirect — the API interceptor handles that.
   *
   * Queued points are KEPT. They used to be purged here, on the reasoning
   * that the server would never take them once auth was gone — but a signed-
   * out operator is coming back, and a session that lapses during a long
   * offline stint is exactly when the backlog is biggest. The queue is
   * stopped (no 401 spam) and `waypointQueue.resumeAfterSignIn()`, from
   * `TrackingWatcher`, sends them once somebody is signed in again.
   */
  async halt(): Promise<void> {
    this.detachLocationListener();
    this.detachQueueStatusListener();
    try { await backgroundLocation.stop(); } catch (e) {
      console.warn('[trackingController] halt: backgroundLocation.stop failed', e);
    }
    waypointQueue.stopAutoSync();

    if (this.pendingTimer != null) {
      clearInterval(this.pendingTimer);
      this.pendingTimer = null;
    }
    this.stopWatchdog();

    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }

    this.setState({
      entity: null,
      tracking: false,
      waypointCount: 0,
      pendingCount: 0,
      lastLocation: null,
      gpsStatus: 'unavailable',
      currentSpeedKmh: null,
      recentSpeeds: [],
      error: null,
    });
    this.speedBuffer = [];
  }

  /**
   * Called by `TrackingWatcher` on app mount. If localStorage has a saved
   * entity, rehydrate the in-memory entity reference and resume the upload
   * queue so any pending IndexedDB points keep draining — but DO NOT restart
   * the platform GPS / foreground service.
   *
   * Why we don't auto-restart GPS:
   *   - A persisted entity does not mean the user wants to be tracking right
   *     now. They may have paused the job, force-killed the app, completed
   *     the job from another device, or simply opened the app to clock in.
   *   - Silently spinning up the foreground GPS notification on the Dashboard
   *     with no operator action is bad UX (and battery).
   *   - The page-level auto-start in `useWaypointTracking` already restarts
   *     GPS the moment the user navigates back to the job/spray page and the
   *     entity transitions to `in_progress`. That's the correct gate.
   *
   * The Dashboard surfaces any paused job via a banner so the operator can
   * navigate to it deliberately.
   */
  async resumeFromStorage(): Promise<void> {
    if (this.state.tracking) return;

    // Tear down any orphan foreground-service notification left over from a
    // prior session. The Capgo plugin runs the service in its own native
    // process, so a cold-started app sees `running=false` JS-side even when
    // the OS still has a pinned notification. forceStopNative is a no-op if
    // there's nothing to stop.
    void backgroundLocation.forceStopNative();

    let saved: TrackingEntity | null = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) saved = JSON.parse(raw) as TrackingEntity;
    } catch {
      return;
    }
    if (!saved || typeof saved.id !== 'number') return;

    // Keep entity in memory (so pendingCount can be associated with the right
    // {type,id} and the page-level start() is a no-op match), but mark not-
    // tracking. No listener attach, no backgroundLocation.start.
    this.setState({ entity: saved, tracking: false, error: null });
    waypointQueue.startAutoSync();

    // Seed pendingCount and kick an upload attempt for anything queued before
    // the reload.
    try {
      const count = await waypointQueue.pendingCount(saved.type, saved.id);
      this.setState({ pendingCount: count });
      void waypointQueue.flush();
    } catch { /* ignore */ }

    if (this.pendingTimer == null) {
      this.pendingTimer = setInterval(() => this.refreshPendingCount(), PENDING_POLL_MS);
    }
  }

  // ---------------------------------------------------------------------------

  private attachLocationListener(): void {
    if (this.locationSubscribed) return;
    this.locationSubscribed = true;
    backgroundLocation.onLocation(this.handleLocation);
  }

  private detachLocationListener(): void {
    if (!this.locationSubscribed) return;
    this.locationSubscribed = false;
    backgroundLocation.offLocation(this.handleLocation);
  }

  private handleLocation = (wp: WaypointInput): void => {
    const entity = this.state.entity;
    if (!entity) return;

    // A fresh fix arrived — reset the watchdog + restart counter.
    this.lastWaypointAt = Date.now();
    this.watchdogRestartAttempts = 0;

    waypointQueue.enqueue(entity.type, entity.id, wp).catch((e) => {
      console.error('[trackingController] enqueue error:', e);
    });

    this.speedBuffer.push(wp.speed ?? null);
    if (this.speedBuffer.length > SPEED_BUFFER_SIZE) this.speedBuffer.shift();

    this.setState({
      waypointCount: this.state.waypointCount + 1,
      lastLocation: { lat: wp.lat, lng: wp.lng },
      gpsStatus: wp.accuracy != null && wp.accuracy < 20 ? 'good' : 'poor',
      currentSpeedKmh: wp.speed != null && wp.speed > 0.3 ? wp.speed * 3.6 : null,
      recentSpeeds: [...this.speedBuffer],
      error: null,
    });
  };

  // ---------------------------------------------------------------------------
  // Watchdog — recover from a silently-dead native callback.
  // ---------------------------------------------------------------------------

  private startWatchdog(): void {
    if (this.watchdogTimer != null) return;
    this.lastWaypointAt = Date.now();
    this.watchdogRestartAttempts = 0;
    this.watchdogTimer = setInterval(() => {
      void this.checkWatchdog();
    }, WATCHDOG_INTERVAL_MS);
  }

  private stopWatchdog(): void {
    if (this.watchdogTimer != null) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
    this.watchdogRestartAttempts = 0;
  }

  private async checkWatchdog(): Promise<void> {
    if (!this.state.tracking) return;
    const since = Date.now() - this.lastWaypointAt;
    if (since < WATCHDOG_STALE_MS) return;
    if (this.watchdogRestartAttempts >= WATCHDOG_MAX_RESTARTS) return;

    this.watchdogRestartAttempts += 1;
    console.warn(
      `[trackingController] watchdog: no GPS fix in ${Math.round(since / 1000)}s — ` +
      `attempting restart (${this.watchdogRestartAttempts}/${WATCHDOG_MAX_RESTARTS})`,
    );
    try {
      // Stop + start the platform GPS without tearing down the controller or
      // draining the queue.
      await backgroundLocation.stop();
    } catch (e) {
      console.warn('[trackingController] watchdog stop failed', e);
    }
    try {
      // The same interval it was started with — a restart must not quietly
      // drop a spray back to the job cadence.
      const entity = this.state.entity;
      await backgroundLocation.start(entity ? fixIntervalFor(entity) : DEFAULT_FIX_INTERVAL_MS);
      this.lastWaypointAt = Date.now(); // reset the grace window post-restart
    } catch (e) {
      console.warn('[trackingController] watchdog start failed', e);
    }
  }

  private async refreshPendingCount(): Promise<void> {
    const entity = this.state.entity;
    if (!entity) return;
    try {
      const count = await waypointQueue.pendingCount(entity.type, entity.id);
      if (count !== this.state.pendingCount) this.setState({ pendingCount: count });
    } catch { /* ignore */ }
  }

  private setState(partial: Partial<TrackingState>): void {
    this.state = { ...this.state, ...partial };
    for (const l of this.listeners) {
      try { l(this.state); } catch (e) { console.error('[trackingController] listener error', e); }
    }
  }
}

const trackingController = new TrackingController();
export default trackingController;
